import { LoadingIndicator, SpinnerSizes } from '@triumph/design-system';
import { forwardRef, Fragment, useId, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useApplicantDueDiligenceQuery } from '@/components/shared/api/queries/applicant';
import { FILE_URL_FRESH_MS, useFileQuery } from '@/components/shared/api/queries/files';
import { CardInformation } from '@/components/shared/components/CardInformation';
import { Linkify } from '@/components/shared/components/Linkify';
import { MaskedComponent } from '@/components/shared/components/MaskedComponent';
import { MaskField } from '@/components/shared/components/MaskedField';
import { TabDataState } from '@/components/shared/components/TabDataState';
import { Feature } from '@/components/shared/constants/features';

import {
    CardChevron,
    CardStack,
    CardToggle,
    CollapsibleCardHeader,
    DDDFileFallbackLink,
    DDDFilePreviewErrorWrapper,
    DDDFilePreviewLoadingWrapper,
    DDDFilePreviewRetryButton,
    DDDFileThumbnailImage,
    DDDFileThumbnailLink,
    DDDCard,
    DDDCardPulled,
    DDDCardSection,
    DDDCardSectionTitle,
    DDDCardTitle,
    DDDGroupHeading,
    DDDRow,
    DDDRowLabel,
    DDDRowGroup,
    DDDRowList,
    DDDRowValue,
} from '../Applicant.StyledComponents';

import type {
    ApplicantDueDiligenceData,
    DueDiligenceDataPoint,
    DueDiligenceSection,
} from '../Applicant.types';

import { getDddBlockAnchorId } from './dddAnchors';
import { VendorStatusTable } from './VendorStatusTable';
import { formatArrival } from './VendorStatusTable/VendorStatusTable.utils';

const SENSITIVE_AU10TIX_LABELS = new Set(['ID Number', 'Date of Birth', 'Gender']);

interface DueDiligenceProps {
    // The print copy mounts a second instance of this tab, so its anchors need ids of their own.
    idPrefix?: string;
}

const DDDFilePreviewLoading = () => (
    <DDDFilePreviewLoadingWrapper data-testid="ddd-file-loading">
        <LoadingIndicator color="#444" size={SpinnerSizes.small} show />
    </DDDFilePreviewLoadingWrapper>
);

const DDDFilePreview = ({ fileId }: { fileId: string }) => {
    const {
        data: presignedUrl,
        isLoading: isUrlLoading,
        isLoadingError: isUrlLoadingError,
        refetch: refetchUrl,
    } = useFileQuery({
        fileId,
        // The thumbnail stays on screen, so it re-fetches its link on return before S3 expires it.
        refetchOnWindowFocus: true,
        staleTime: FILE_URL_FRESH_MS,
    });
    const [imageFailed, setImageFailed] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    if (isUrlLoading) return <DDDFilePreviewLoading />;

    if (isUrlLoadingError) {
        return (
            <DDDFilePreviewErrorWrapper role="status" data-testid={`ddd-file-error-${fileId}`}>
                Photo ID failed to load
                <DDDFilePreviewRetryButton
                    type="button"
                    aria-label="Retry loading photo ID"
                    onClick={() => refetchUrl()}
                >
                    Retry
                </DDDFilePreviewRetryButton>
            </DDDFilePreviewErrorWrapper>
        );
    }

    if (!presignedUrl) {
        return (
            <DDDFilePreviewErrorWrapper role="status" data-testid={`ddd-file-unavailable-${fileId}`}>
                Photo ID unavailable
            </DDDFilePreviewErrorWrapper>
        );
    }

    if (imageFailed) {
        return (
            <DDDFileFallbackLink
                href={presignedUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`ddd-file-link-${fileId}`}
            >
                View source document
            </DDDFileFallbackLink>
        );
    }

    return (
        <>
            {imageLoaded ? null : <DDDFilePreviewLoading />}
            <DDDFileThumbnailLink
                href={presignedUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`ddd-file-link-${fileId}`}
                style={imageLoaded ? undefined : { display: 'none' }}
            >
                <DDDFileThumbnailImage
                    src={presignedUrl}
                    alt="Source document preview"
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageFailed(true)}
                />
            </DDDFileThumbnailLink>
        </>
    );
};

// Labels repeat within a block (every Middesk verdict row in a category can share one), so the index keeps keys unique.
const DDDRows = ({ data }: { data: DueDiligenceDataPoint[] }) => (
    <>
        {data.map((d, index) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <DDDRow data-testid="due-diligence-cntent" key={`[${index}-${d.label}-${d.value}]`}>
                <DDDRowLabel>{d.label}</DDDRowLabel>
                <DDDRowValue>
                    {d?.value == null ? (
                        'Unknown'
                    ) : SENSITIVE_AU10TIX_LABELS.has(d.label) ? (
                        <MaskField value={d.value} requiredFeatures={[Feature.CanSeeSensitiveData]} />
                    ) : (
                        <Linkify>{d.value}</Linkify>
                    )}
                </DDDRowValue>
            </DDDRow>
        ))}
    </>
);

const DDDSections = ({ sections }: { sections: DueDiligenceSection[] }) => (
    <>
        {sections.map((section, sectionIndex) => (
            // Derived, read-only vendor sections with no stable id.
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <DDDCardSection key={`ddd-section-${sectionIndex}`} data-testid="due-diligence-section">
                <DDDCardSectionTitle>
                    {section.count == null ? section.title : `${section.title} (${section.count})`}
                </DDDCardSectionTitle>
                <DDDRowList>
                    {section.groups.map((group, groupIndex) => (
                        // eslint-disable-next-line @eslint-react/no-array-index-key
                        <Fragment key={`ddd-group-${sectionIndex}-${groupIndex}`}>
                            {group.heading ? <DDDGroupHeading>{group.heading}</DDDGroupHeading> : null}
                            <DDDRowGroup>
                                <DDDRows data={group.data} />
                            </DDDRowGroup>
                        </Fragment>
                    ))}
                </DDDRowList>
            </DDDCardSection>
        ))}
    </>
);

export const DDDBlockBody = ({ item }: { item: ApplicantDueDiligenceData }) => {
    const { fileId } = item;

    return (
        <>
            {item.data.length > 0 || fileId ? (
                <DDDRowList>
                    <DDDRows data={item.data} />
                    {fileId ? (
                        <DDDRow>
                            <DDDRowLabel>Photo ID</DDDRowLabel>
                            <DDDRowValue>
                                <MaskedComponent
                                    placeholderText="Photo ID hidden"
                                    requiredFeatures={[Feature.CanSeeSensitiveData]}
                                >
                                    <DDDFilePreview fileId={fileId} />
                                </MaskedComponent>
                            </DDDRowValue>
                        </DDDRow>
                    ) : null}
                </DDDRowList>
            ) : null}
            {item.sections ? <DDDSections sections={item.sections} /> : null}
        </>
    );
};

interface CollapsedCards {
    applicantId?: string;
    indexes: ReadonlySet<number>;
}

interface DDDGroupCardProps {
    item: ApplicantDueDiligenceData;
    anchorId: string | null;
    isOpen: boolean;
    onToggle: () => void;
    testId: string;
}

const DDDGroupCard = ({ item, anchorId, isOpen, onToggle, testId }: DDDGroupCardProps) => {
    const bodyId = useId();

    return (
        <DDDCard id={anchorId ?? undefined} data-testid="due-diligence-group">
            <CollapsibleCardHeader $collapsed={!isOpen}>
                <DDDCardTitle>
                    <CardToggle
                        type="button"
                        onClick={onToggle}
                        aria-expanded={isOpen}
                        aria-controls={bodyId}
                        data-testid={`${testId}-toggle`}
                    >
                        <CardChevron aria-hidden>{isOpen ? '▾' : '▸'}</CardChevron>
                        {item.title}
                    </CardToggle>
                </DDDCardTitle>
                {item.pulledAt ? (
                    <DDDCardPulled>{formatArrival(item.pulledAt)}</DDDCardPulled>
                ) : null}
            </CollapsibleCardHeader>
            {/* Hidden rather than unmounted so a revealed masked field stays revealed across a collapse. */}
            <div id={bodyId} hidden={!isOpen}>
                <DDDBlockBody item={item} />
            </div>
        </DDDCard>
    );
};

export const DueDiligence = forwardRef<HTMLDivElement, DueDiligenceProps>(({ idPrefix = '' }, ref) => {
    const { applicantId } = useParams();

    const {
        data: dueDiligenceData,
        isLoading,
        isLoadingError,
        refetch,
    } = useApplicantDueDiligenceQuery({
        applicantId: applicantId ?? '',
    });

    const dueDiligenceGroups = dueDiligenceData ?? [];

    // Keyed by applicant so moving to another applicant starts with every card expanded.
    const [collapsed, setCollapsed] = useState<CollapsedCards>(() => ({ indexes: new Set() }));
    const collapsedIndexes =
        collapsed.applicantId === applicantId ? collapsed.indexes : new Set<number>();
    const setCardCollapsed = (index: number, isCollapsed: boolean) => {
        const next = new Set(collapsedIndexes);
        if (isCollapsed) next.add(index);
        else next.delete(index);
        setCollapsed({ applicantId, indexes: next });
    };
    const expandCardsWithAnchor = (anchorId: string) => {
        const next = [...collapsedIndexes].filter((index) => {
            const item = dueDiligenceGroups[index];
            return getDddBlockAnchorId(item?.vendor, item?.ownerId, idPrefix) !== anchorId;
        });
        setCollapsed({ applicantId, indexes: new Set(next) });
    };

    const blockAnchorIds = new Set(
        dueDiligenceGroups
            .map((item) => getDddBlockAnchorId(item.vendor, item.ownerId, idPrefix))
            .filter((anchorId): anchorId is string => anchorId !== null),
    );

    const renderDueDiligence = () =>
        dueDiligenceGroups.map((item, index) => (
            <DDDGroupCard
                // eslint-disable-next-line @eslint-react/no-array-index-key -- vendor/owner pairs can repeat
                key={`due-diligence-item-${index}`}
                item={item}
                anchorId={getDddBlockAnchorId(item.vendor, item.ownerId, idPrefix)}
                isOpen={!collapsedIndexes.has(index)}
                onToggle={() => setCardCollapsed(index, !collapsedIndexes.has(index))}
                testId={`due-diligence-group-${index}`}
            />
        ));

    if (!applicantId) {
        return <></>;
    }

    return (
        <CardInformation>
            <VendorStatusTable
                blockAnchorIds={blockAnchorIds}
                idPrefix={idPrefix}
                onViewDdd={expandCardsWithAnchor}
            />
            <CardStack id="printSection" ref={ref}>
                <TabDataState
                    isLoading={isLoading}
                    isLoadingError={isLoadingError}
                    onRetry={() => refetch()}
                    isEmpty={dueDiligenceGroups.length === 0}
                    emptyMessage="No DDD found for applicant"
                    errorMessage="Could not load due diligence data."
                >
                    {renderDueDiligence()}
                </TabDataState>
            </CardStack>
        </CardInformation>
    );
});
